import React, { useEffect, useRef, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Star,
  Search,
  Ban,
  CreditCard,
  HardDrive,
  Tag,
  Gift,
  Share2,
  Copy,
  Download,
  MessageCircle,
  Mail,
  ChevronDown,
  Facebook,
  Twitter,
  Megaphone,
} from "lucide-react";

import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";

import { userPlansApi, staticDataApi, couponsApi, ApiError } from "../utils/api";
import { CouponPreview } from "./CouponPreview";
import { RichTextEditor } from "./RichTextEditor";
import {
  StatusBadge,
  TableSkeleton,
  ErrorBanner,
  formatBytes,
  formatDate,
  TAB_ACCENTS,
  TabIconBadge,
} from "./StaticDataManager";

const gbToBytes = (gb: number) => Math.round(gb * 1024 * 1024 * 1024);
const bytesToGb = (bytes: number) => {
  if (!bytes) return "";
  const gb = bytes / (1024 * 1024 * 1024);
  return Number.isInteger(gb) ? String(gb) : String(Number(gb.toFixed(2)));
};

const errorMessage = (err: unknown, fallback: string) =>
  err instanceof ApiError ? err.message : fallback;

// ─────────────────────────────────────────────────────────────────────────
// Plans Tab — /user-plans
// ─────────────────────────────────────────────────────────────────────────

const emptyPlanForm = {
  planName: "",
  description: "",
  userType: "athlete",
  paymentType: "recurring",
  paymentInterval: "month",
  paymentAmount: "",
  oneTimeFee: "",
  allowedStorageGb: "",
  featuresText: "",
  maxUsers: "",
  supportLevel: "basic",
};

export function PlansTab() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [plans, setPlans] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...emptyPlanForm });

  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);

  const isFirstSearch = useRef(true);

  const load = async (page = 1) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await userPlansApi.getAll({
        page,
        limit: 10,
        search: searchTerm || undefined,
      });

      if (response.success) {
        setPlans(response.data.plans || []);
        setPagination(response.data.pagination || null);
      } else {
        setError(response.message || "Failed to load plans");
      }
    } catch (err) {
      const message = err instanceof ApiError
        ? `${err.message} (${err.status})`
        : "Failed to load plans";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (isFirstSearch.current) {
      isFirstSearch.current = false;
      return;
    }
    const timeoutId = setTimeout(() => {
      if (currentPage === 1) {
        load(1);
      } else {
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const totalItems = pagination?.totalItems ?? plans.length;
  const totalPages = pagination?.totalPages ?? 1;

  const openAdd = () => {
    setEditItem(null);
    setForm({ ...emptyPlanForm });
    setDialogOpen(true);
  };

  const openEdit = (plan: any) => {
    setEditItem(plan);
    setForm({
      planName: plan.planName || "",
      description: plan.description || "",
      userType: plan.userType || "athlete",
      paymentType: plan.paymentType || "recurring",
      paymentInterval: plan.paymentInterval || "month",
      paymentAmount: plan.paymentAmount != null ? String(plan.paymentAmount) : "",
      oneTimeFee: plan.oneTimeFee != null ? String(plan.oneTimeFee) : "",
      allowedStorageGb: bytesToGb(plan.allowedStorage),
      featuresText: Array.isArray(plan.features) ? plan.features.join("\n") : "",
      maxUsers: plan.maxUsers != null ? String(plan.maxUsers) : "",
      supportLevel: plan.supportLevel || "basic",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.planName.trim() || !form.userType) {
      toast.error("Plan name and user type are required");
      return;
    }

    setSaving(true);

    try {
      const payload: any = {
        planName: form.planName.trim(),
        description: form.description.trim(),
        userType: form.userType,
        paymentType: form.paymentType,
        paymentAmount: form.paymentAmount ? Number(form.paymentAmount) : 0,
        oneTimeFee: form.oneTimeFee ? Number(form.oneTimeFee) : 0,
        allowedStorage: form.allowedStorageGb ? gbToBytes(Number(form.allowedStorageGb)) : 0,
        features: form.featuresText
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean),
        supportLevel: form.supportLevel,
      };

      if (form.paymentType === "recurring") {
        payload.paymentInterval = form.paymentInterval;
      }

      if (form.maxUsers !== "") {
        payload.maxUsers = Number(form.maxUsers);
      }

      const response = editItem
        ? await userPlansApi.update(editItem._id, payload)
        : await userPlansApi.create(payload);

      if (response.success) {
        toast.success(response.message || (editItem ? "Plan updated" : "Plan created"));
        setDialogOpen(false);
        load(currentPage);
      } else {
        toast.error(response.message || "Save failed");
      }
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save plan"));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (plan: any) => {
    try {
      const response = await userPlansApi.toggleStatus(plan._id);
      toast[response.success ? "success" : "error"](
        response.message || (plan.disabled ? "Plan enabled" : "Plan disabled")
      );
      load(currentPage);
    } catch (err) {
      toast.error(errorMessage(err, "Failed to update plan status"));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      const response = await userPlansApi.delete(deleteTarget._id);
      toast[response.success ? "success" : "error"](
        response.message || "Plan deleted permanently"
      );
      if (response.success) {
        setDeleteTarget(null);
        load(currentPage);
      }
    } catch (err) {
      toast.error(errorMessage(err, "Failed to delete plan"));
    }
  };

  const paymentDisplay = (plan: any) => {
    if (plan.paymentType === "one_time") {
      return `$${Number(plan.oneTimeFee || 0).toFixed(2)} one-time`;
    }
    const amount = Number(plan.paymentAmount || 0);
    if (amount === 0) return "Free";
    return `$${amount.toFixed(2)}/${plan.paymentInterval || "month"}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TabIconBadge icon={CreditCard} accent="plans" />
          <div>
            <h2 className="text-lg font-semibold">Plans</h2>
            <p className="text-sm text-muted-foreground">
              Subscription plans available on the platform — {totalItems} total.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={openAdd} className={TAB_ACCENTS.plans.button}>
            <Plus className="h-4 w-4 mr-2" />
            Add Plan
          </Button>
        </div>
      </div>

      <div className="relative w-64">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search plans..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          className="pl-8"
        />
      </div>

      {error && <ErrorBanner message={error} onRetry={() => load(currentPage)} />}

      {isLoading ? (
        <TableSkeleton cols={7} />
      ) : (
        <Card className={TAB_ACCENTS.plans.border}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>User Type</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Setup Fee</TableHead>
                <TableHead>Storage</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {plans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                    No plans found.
                  </TableCell>
                </TableRow>
              ) : (
                plans.map((plan: any) => (
                  <TableRow key={plan._id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{plan.planName}</span>
                        {plan.isDefault && (
                          <Star className="h-4 w-4 text-yellow-500 fill-current shrink-0" />
                        )}
                      </div>
                      {plan.description && (
                        <p className="text-xs text-muted-foreground line-clamp-1 max-w-[240px]">
                          {plan.description}
                        </p>
                      )}
                    </TableCell>

                    <TableCell>
                      <Badge variant="secondary">
                        {plan.userType.charAt(0).toUpperCase() + plan.userType.slice(1).toLowerCase()}
                      </Badge>
                    </TableCell>

                    <TableCell className="text-sm">{paymentDisplay(plan)}</TableCell>

                    <TableCell className="text-sm text-muted-foreground">
                      {plan.oneTimeFee ? `$${Number(plan.oneTimeFee).toFixed(2)}` : "—"}
                    </TableCell>

                    <TableCell className="text-sm">
                      {plan.allowedStorage ? formatBytes(plan.allowedStorage) : "—"}
                    </TableCell>

                    <TableCell>
                      <StatusBadge disabled={!!plan.disabled} />
                    </TableCell>

                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />

                          <DropdownMenuItem onClick={() => openEdit(plan)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>

                          <DropdownMenuItem onClick={() => handleToggleStatus(plan)}>
                            <Ban className="h-4 w-4 mr-2" />
                            {plan.disabled ? "Enable" : "Disable"}
                          </DropdownMenuItem>

                          <DropdownMenuSeparator />

                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setDeleteTarget(plan)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete permanently
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages} — {totalItems} plans
          </p>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>

            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editItem ? "Edit Plan" : "Add Plan"}</DialogTitle>
            <DialogDescription>
              {editItem
                ? "Update the plan details."
                : "Stripe product & price are created automatically — no need to enter them here."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="plan-name">Plan Name</Label>
              <Input
                id="plan-name"
                value={form.planName}
                onChange={(event) => setForm((current) => ({ ...current, planName: event.target.value }))}
                placeholder="e.g. Pro Coach"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="plan-description">Description</Label>
              <Textarea
                id="plan-description"
                value={form.description}
                onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                placeholder="Short description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>User Type</Label>
                <Select
                  value={form.userType}
                  onValueChange={(value) => setForm((current) => ({ ...current, userType: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="athlete">Athlete</SelectItem>
                    <SelectItem value="coach">Coach</SelectItem>
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="ORGANIZATION">Organization</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label>Support Level</Label>
                <Select
                  value={form.supportLevel}
                  onValueChange={(value) => setForm((current) => ({ ...current, supportLevel: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label>Payment Type</Label>
              <RadioGroup
                className="flex gap-6"
                value={form.paymentType}
                onValueChange={(value) => setForm((current) => ({ ...current, paymentType: value }))}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="recurring" id="payment-recurring" />
                  <Label htmlFor="payment-recurring" className="font-normal">Recurring</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="one_time" id="payment-one-time" />
                  <Label htmlFor="payment-one-time" className="font-normal">One-time</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="plan-amount">
                  {form.paymentType === "recurring" ? "Price ($)" : "One-time Price ($)"}
                </Label>
                <Input
                  id="plan-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.paymentAmount}
                  onChange={(event) => setForm((current) => ({ ...current, paymentAmount: event.target.value }))}
                  placeholder="0.00"
                />
              </div>

              {form.paymentType === "recurring" && (
                <div className="space-y-1">
                  <Label>Billing Interval</Label>
                  <Select
                    value={form.paymentInterval}
                    onValueChange={(value) => setForm((current) => ({ ...current, paymentInterval: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="day">Daily</SelectItem>
                      <SelectItem value="week">Weekly</SelectItem>
                      <SelectItem value="month">Monthly</SelectItem>
                      <SelectItem value="year">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="plan-onetime-fee">Setup / One-time Fee ($)</Label>
                <Input
                  id="plan-onetime-fee"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.oneTimeFee}
                  onChange={(event) => setForm((current) => ({ ...current, oneTimeFee: event.target.value }))}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="plan-storage">Allowed Storage (GB)</Label>
                <Input
                  id="plan-storage"
                  type="number"
                  min="0"
                  step="0.1"
                  value={form.allowedStorageGb}
                  onChange={(event) => setForm((current) => ({ ...current, allowedStorageGb: event.target.value }))}
                  placeholder="e.g. 2"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="plan-max-users">Max Users</Label>
              <Input
                id="plan-max-users"
                type="number"
                min="0"
                value={form.maxUsers}
                onChange={(event) => setForm((current) => ({ ...current, maxUsers: event.target.value }))}
                placeholder="Leave blank for unlimited"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="plan-features">Features (one per line)</Label>
              <Textarea
                id="plan-features"
                value={form.featuresText}
                onChange={(event) => setForm((current) => ({ ...current, featuresText: event.target.value }))}
                placeholder={"Unlimited critiques\nPriority support"}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>

            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : editItem ? "Save Changes" : "Create Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open: boolean) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Plan Permanently</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete{" "}
              <strong>{deleteTarget?.planName}</strong>? This also archives
              its Stripe product and price, and cannot be undone. If you just
              want to hide it, use Disable instead.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Storage Plans Tab — /static/storage-plans
// ─────────────────────────────────────────────────────────────────────────

export function StoragePlansTab() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [plans, setPlans] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<any | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({ key: "", label: "", gb: "", price: "" });

  const isFirstSearch = useRef(true);

  const load = async (page = 1) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await staticDataApi.getStoragePlans({
        page,
        limit: 10,
        search: searchTerm || undefined,
      });

      if (response.success) {
        const raw = response.data;
        setPlans(Array.isArray(raw) ? raw : raw?.storagePlans ?? raw?.plans ?? []);
        setPagination(raw?.pagination ?? null);
      } else {
        setError(response.message || "Failed to load storage plans");
      }
    } catch (err) {
      const message = err instanceof ApiError
        ? `${err.message} (${err.status})`
        : "Failed to load storage plans";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (isFirstSearch.current) {
      isFirstSearch.current = false;
      return;
    }
    const timeoutId = setTimeout(() => {
      if (currentPage === 1) {
        load(1);
      } else {
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const totalItems = pagination?.totalItems ?? plans.length;
  const totalPages = pagination?.totalPages ?? 1;

  const openAdd = () => {
    setEditItem(null);
    setForm({ key: "", label: "", gb: "", price: "" });
    setDialogOpen(true);
  };

  const openEdit = (plan: any) => {
    setEditItem(plan);
    setForm({
      key: plan.key ?? "",
      label: plan.label ?? "",
      gb: plan.gb != null ? String(plan.gb) : "",
      price: plan.price != null ? String(plan.price) : "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.key.trim() || !form.label.trim()) {
      toast.error("Key and label are required");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        key: form.key.trim(),
        label: form.label.trim(),
        gb: form.gb ? Number(form.gb) : 0,
        price: form.price ? Number(form.price) : 0,
      };

      const response = editItem
        ? await staticDataApi.updateStoragePlan(editItem._id, payload)
        : await staticDataApi.createStoragePlan(payload);

      toast[response.success ? "success" : "error"](
        response.message || (editItem ? "Storage plan updated" : "Storage plan created")
      );

      if (response.success) {
        setDialogOpen(false);
        load(currentPage);
      }
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save storage plan"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      const response = await staticDataApi.deleteStoragePlan(deleteTarget._id);
      toast[response.success ? "success" : "error"](
        response.message || "Storage plan deleted permanently"
      );
      if (response.success) {
        setDeleteTarget(null);
        load(currentPage);
      }
    } catch (err) {
      toast.error(errorMessage(err, "Failed to delete storage plan"));
    }
  };

  const handleToggleStatus = async (plan: any) => {
    try {
      const response = await staticDataApi.toggleStoragePlanStatus(plan._id);
      toast[response.success ? "success" : "error"](
        response.message || (plan.disabled ? "Storage plan enabled" : "Storage plan disabled")
      );
      load(currentPage);
    } catch (err) {
      toast.error(errorMessage(err, "Failed to update storage plan status"));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TabIconBadge icon={HardDrive} accent="storage-plans" />
          <div>
            <h2 className="text-lg font-semibold">Storage Plans</h2>
            <p className="text-sm text-muted-foreground">
              Available storage add-on tiers — {totalItems} total.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={openAdd} className={TAB_ACCENTS["storage-plans"].button}>
            <Plus className="h-4 w-4 mr-2" />
            Add Storage Plan
          </Button>
        </div>
      </div>

      <div className="relative w-64">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search storage plans..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          className="pl-8"
        />
      </div>

      {error && <ErrorBanner message={error} onRetry={() => load(currentPage)} />}

      {isLoading ? (
        <TableSkeleton cols={6} />
      ) : (
        <Card className={TAB_ACCENTS["storage-plans"].border}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Key</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Storage</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {plans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    No storage plans found.
                  </TableCell>
                </TableRow>
              ) : (
                plans.map((plan: any, index: number) => (
                  <TableRow key={plan._id || index}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        {plan.key}
                      </Badge>
                    </TableCell>

                    <TableCell className="font-medium">{plan.label}</TableCell>

                    <TableCell>{plan.gb != null ? `${plan.gb} GB` : "—"}</TableCell>

                    <TableCell>{plan.price != null ? `$${plan.price}` : "—"}</TableCell>

                    <TableCell>
                      <StatusBadge disabled={!!plan.disabled} />
                    </TableCell>

                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />

                          <DropdownMenuItem onClick={() => openEdit(plan)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>

                          <DropdownMenuItem onClick={() => handleToggleStatus(plan)}>
                            <Ban className="h-4 w-4 mr-2" />
                            {plan.disabled ? "Enable" : "Disable"}
                          </DropdownMenuItem>

                          <DropdownMenuSeparator />

                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setDeleteTarget(plan)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete permanently
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages} — {totalItems} storage plans
          </p>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>

            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editItem ? "Edit Storage Plan" : "Add Storage Plan"}</DialogTitle>
            <DialogDescription>
              {editItem
                ? "Update the storage plan details."
                : "Stripe product & price are created automatically — no need to enter them here."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="storage-key">Key</Label>
              <Input
                id="storage-key"
                value={form.key}
                onChange={(event) => setForm((current) => ({ ...current, key: event.target.value }))}
                placeholder="e.g. storage_10gb"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="storage-label">Label</Label>
              <Input
                id="storage-label"
                value={form.label}
                onChange={(event) => setForm((current) => ({ ...current, label: event.target.value }))}
                placeholder="e.g. 10 GB Add-on"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="storage-gb">Storage (GB)</Label>
                <Input
                  id="storage-gb"
                  type="number"
                  min="0"
                  value={form.gb}
                  onChange={(event) => setForm((current) => ({ ...current, gb: event.target.value }))}
                  placeholder="e.g. 10"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="storage-price">Price ($)</Label>
                <Input
                  id="storage-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={(event) => setForm((current) => ({ ...current, price: event.target.value }))}
                  placeholder="e.g. 4.99"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>

            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open: boolean) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Storage Plan Permanently</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete{" "}
              <strong>{deleteTarget?.label}</strong>? This also archives its
              Stripe product and price, and cannot be undone. If you just want
              to hide it, use Disable instead.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Coupons Tab — /coupons
//
// Split into two kinds that share the same Coupon model but mean different
// things: a "Discount" coupon knocks money off a purchase (% or $, synced to
// Stripe), while a "Free Grant" hands out licenses/storage/access time
// directly (isFreeGrant: true) and is never a Stripe promo code.
// ─────────────────────────────────────────────────────────────────────────

// Share Link generation/QR/social-share is identical for both coupon kinds,
// so it lives here once instead of being duplicated in both tabs.
function ShareCouponDialog({
  coupon,
  open,
  onOpenChange,
  onSaved,
}: {
  coupon: any | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}) {
  const [shareData, setShareData] = useState<{
    shareLink: string;
    qrCodeDataUri: string;
    couponCode: string;
    couponName: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [savingContent, setSavingContent] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (!open || !coupon) {
      setShareData(null);
      return;
    }

    setSubject(coupon.couponSubject || "");
    setContent(coupon.couponMessage || "");

    let cancelled = false;
    setLoading(true);
    setShareData(null);

    (async () => {
      try {
        const response = await couponsApi.getShareLink(coupon._id);
        if (cancelled) return;
        if (response.success) {
          setShareData(response.data);
        } else {
          toast.error(response.message || "Failed to generate share link");
          onOpenChange(false);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(errorMessage(err, "Failed to generate share link"));
          onOpenChange(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, coupon?._id]);

  const saveContent = async () => {
    if (!coupon) return;
    setSavingContent(true);
    try {
      const response = await couponsApi.update(coupon._id, {
        couponSubject: subject.trim(),
        couponMessage: content.trim(),
      });
      if (response.success) {
        toast.success("Marketing content saved");
        onSaved?.();
      } else {
        toast.error(response.message || "Failed to save marketing content");
      }
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save marketing content"));
    } finally {
      setSavingContent(false);
    }
  };

  // Content is authored as HTML (RichTextEditor's contentEditable output) -
  // plain-text share channels (WhatsApp, native share, email/social body
  // text) need the tags stripped or they'd show up as literal text.
  const stripHtml = (html: string) => html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

  const shareText = () => {
    const plainMessage = stripHtml(content);
    return `${coupon?.orgcoupon_name || "Coachable coupon"}${
      plainMessage ? ` — ${plainMessage}` : ""
    }\n${shareData?.shareLink || ""}`;
  };

  const copyShareLink = async () => {
    if (!shareData) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareData.shareLink);
      } else {
        // navigator.clipboard requires a secure context (HTTPS/localhost);
        // fall back to the legacy copy command when served over plain HTTP.
        // Select from the existing share-link input rather than a detached
        // element - the Dialog traps focus within itself, so focusing an
        // element appended to document.body gets immediately reverted.
        const input = document.getElementById("share-link") as HTMLInputElement | null;
        if (!input) throw new Error("share-link input not found");
        input.focus();
        input.select();
        const copied = document.execCommand("copy");
        if (!copied) throw new Error("execCommand copy failed");
      }
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const downloadQrCode = () => {
    if (!shareData) return;
    const link = document.createElement("a");
    link.href = shareData.qrCodeDataUri;
    link.download = `${shareData.couponCode || "coupon"}-qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const canUseNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  const shareQrCode = async () => {
    if (!shareData) return;
    try {
      const res = await fetch(shareData.qrCodeDataUri);
      const blob = await res.blob();
      const file = new File([blob], `${shareData.couponCode || "coupon"}-qr.png`, {
        type: blob.type || "image/png",
      });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: coupon?.orgcoupon_name || "Coachable coupon",
          text: shareText(),
        });
        return;
      }

      if (navigator.share) {
        await navigator.share({
          title: coupon?.orgcoupon_name || "Coachable coupon",
          text: shareText(),
          url: shareData.shareLink,
        });
      }
    } catch (err) {
      if ((err as any)?.name !== "AbortError") {
        toast.error(errorMessage(err, "Failed to share QR code"));
      }
    }
  };

  const shareViaWhatsApp = () => {
    if (!shareData) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText())}`, "_blank");
  };

  const shareViaEmail = async () => {
    if (!shareData) return;
    const rawSubject = subject.trim() || coupon?.orgcoupon_name || "Coachable coupon";
    const rawBody = shareText();

    // mailto: only does anything if the browser/OS has a default mail client
    // registered - with none configured, clicking this silently does
    // nothing and looks broken. Copying the content first means the admin
    // always has something useful to paste into Gmail/webmail regardless of
    // whether a mail app actually opens.
    try {
      await navigator.clipboard.writeText(`${rawSubject}\n\n${rawBody}`);
      toast.success("Email content copied — paste it into your mail client");
    } catch {
      // clipboard access can fail (permissions, insecure context) - the
      // mailto attempt below is still worth trying either way.
    }

    window.location.href = `mailto:?subject=${encodeURIComponent(rawSubject)}&body=${encodeURIComponent(rawBody)}`;
  };

  const shareViaFacebook = () => {
    if (!shareData) return;
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareData.shareLink)}`,
      "_blank"
    );
  };

  const shareViaTwitter = () => {
    if (!shareData) return;
    const plainMessage = stripHtml(content);
    const text = `${coupon?.orgcoupon_name || "Coachable coupon"}${
      plainMessage ? ` — ${plainMessage}` : ""
    }`;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(
        shareData.shareLink
      )}`,
      "_blank"
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Share Coupon</DialogTitle>
          <DialogDescription>
            {coupon?.orgcoupon_name} ({coupon?.coupon_code})
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Generating link...
          </div>
        ) : shareData ? (
          <div className="space-y-4 py-2">
            <div className="flex justify-center">
              <img
                src={shareData.qrCodeDataUri}
                alt="Coupon QR code"
                className="h-48 w-48 rounded border"
              />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={downloadQrCode}>
                <Download className="h-4 w-4 mr-2" />
                Download QR
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex-1">
                    <Share2 className="h-4 w-4 mr-2" />
                    Share QR
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canUseNativeShare && (
                    <>
                      <DropdownMenuItem onClick={shareQrCode}>
                        <Share2 className="h-4 w-4 mr-2" />
                        Share via device...
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={shareViaWhatsApp}>
                    <MessageCircle className="h-4 w-4 mr-2" />
                    WhatsApp
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={shareViaEmail}>
                    <Mail className="h-4 w-4 mr-2" />
                    Email
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={copyShareLink}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Link
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="space-y-1">
              <Label htmlFor="share-link">Share Link</Label>
              <div className="flex gap-2">
                <Input id="share-link" readOnly value={shareData.shareLink} className="text-xs" />
                <Button variant="outline" size="icon" onClick={copyShareLink} title="Copy link">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-3 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <p className="flex items-center gap-2 text-sm font-medium">
                  <Megaphone className="h-4 w-4" />
                  Marketing Content
                </p>
                <Button variant="ghost" size="sm" onClick={() => setPreviewOpen(true)}>
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Button>
              </div>

              <div className="space-y-1">
                <Label htmlFor="share-subject">Subject</Label>
                <Input
                  id="share-subject"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  placeholder="Enter subject"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="share-content">Marketing Message</Label>
                <RichTextEditor
                  id="share-content"
                  value={content}
                  onChange={setContent}
                  placeholder="Enter your message here..."
                />
              </div>

              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={
                    savingContent ||
                    (content.trim() === (coupon?.couponMessage || "") &&
                      subject.trim() === (coupon?.couponSubject || ""))
                  }
                  onClick={saveContent}
                >
                  {savingContent ? "Saving..." : "Save"}
                </Button>
              </div>

              <div className="space-y-1 border-t pt-3">
                <Label>Share</Label>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="flex-1 bg-[#1877F2] text-white hover:bg-[#1877F2]/90 hover:text-white"
                    title="Share on Facebook"
                    onClick={shareViaFacebook}
                  >
                    <Facebook className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="flex-1 bg-[#1DA1F2] text-white hover:bg-[#1DA1F2]/90 hover:text-white"
                    title="Share on Twitter"
                    onClick={shareViaTwitter}
                  >
                    <Twitter className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="flex-1 bg-destructive text-white hover:bg-destructive/90 hover:text-white"
                    title="Share via Email"
                    onClick={shareViaEmail}
                  >
                    <Mail className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>

      <CouponPreview
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        couponName={coupon?.orgcoupon_name || ""}
        couponCode={coupon?.coupon_code || ""}
        thumbnailUrl={coupon?.thumbnailPath}
        subject={subject}
        message={content}
        description={coupon?.description}
        isFreeGrant={!!coupon?.isFreeGrant}
        discountType={coupon?.discountType}
        discountPercent={coupon?.discountPercent}
        discountAmount={coupon?.discountAmount}
        licenses={coupon?.licenses}
        dataLimitGB={coupon?.dataLimitGB}
        durationDays={coupon?.durationDays}
        startsAt={coupon?.starts_at}
        expiresAt={coupon?.expires_at}
        usageLimit={coupon?.usage_limit}
        limitUsed={coupon?.limit_used}
        organizationIds={coupon?.organizationIds || []}
        disabled={!!coupon?.disabled}
        perUserLimit={coupon?.rules?.perUserLimit}
        minimumOrderValue={coupon?.rules?.minimumOrderValue}
      />
    </Dialog>
  );
}

// Delete confirmation (with the "disable instead" escape hatch for coupons
// that have already been redeemed) is identical for both coupon kinds.
function DeleteCouponDialog({
  coupon,
  onOpenChange,
  onDeleted,
  onDisableInstead,
}: {
  coupon: any | null;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
  onDisableInstead: (coupon: any) => Promise<void>;
}) {
  const handleDelete = async () => {
    if (!coupon) return;
    try {
      const response = await couponsApi.delete(coupon._id);
      toast[response.success ? "success" : "error"](response.message || "Coupon deleted");
      if (response.success) {
        onDeleted();
      }
    } catch (err) {
      toast.error(errorMessage(err, "Failed to delete coupon — try disabling it instead"));
    }
  };

  const handleDisableInstead = async () => {
    if (!coupon) return;
    await onDisableInstead(coupon);
    onOpenChange(false);
  };

  return (
    <AlertDialog open={!!coupon} onOpenChange={(open: boolean) => !open && onOpenChange(false)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Coupon Permanently</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to permanently delete{" "}
            <strong>{coupon?.orgcoupon_name}</strong>?
            {coupon?.limit_used > 0 && (
              <>
                {" "}This coupon has already been used {coupon.limit_used} time
                {coupon.limit_used === 1 ? "" : "s"} — deletion may be blocked. Consider
                disabling it instead.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          {coupon?.limit_used > 0 && (
            <Button variant="outline" onClick={handleDisableInstead}>
              Disable Instead
            </Button>
          )}
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={handleDelete}
          >
            Delete Permanently
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const getCouponStatusKey = (coupon: any): "disabled" | "expired" | "active" => {
  if (coupon.disabled) return "disabled";
  if (coupon.expires_at && new Date(coupon.expires_at) < new Date()) return "expired";
  return "active";
};

export function CouponsTab() {
  const [coupons, setCoupons] = useState<any[]>([]);
  const [filteredCoupons, setFilteredCoupons] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [searchName, setSearchName] = useState("");
  const [searchCode, setSearchCode] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [minUsageLimit, setMinUsageLimit] = useState("");
  const [maxUsageLimit, setMaxUsageLimit] = useState("");

  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [shareTarget, setShareTarget] = useState<any | null>(null);

  const load = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await couponsApi.getAll({ page: 1, limit: 500 });

      if (response.success) {
        setCoupons(response.data.coupons || []);
      } else {
        setError(response.message || "Failed to load coupons");
      }
    } catch (err) {
      const message = err instanceof ApiError
        ? `${err.message} (${err.status})`
        : "Failed to load coupons";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    let filtered = [...coupons];

    if (searchName.trim()) {
      const term = searchName.trim().toLowerCase();
      filtered = filtered.filter((coupon) =>
        (coupon.orgcoupon_name || "").toLowerCase().includes(term)
      );
    }

    if (searchCode.trim()) {
      const term = searchCode.trim().toLowerCase();
      filtered = filtered.filter((coupon) =>
        (coupon.coupon_code || "").toLowerCase().includes(term)
      );
    }

    if (typeFilter !== "all") {
      const wantFreeGrant = typeFilter === "free-grant";
      filtered = filtered.filter((coupon) => !!coupon.isFreeGrant === wantFreeGrant);
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((coupon) => getCouponStatusKey(coupon) === statusFilter);
    }

    if (minUsageLimit) {
      const min = Number(minUsageLimit);
      filtered = filtered.filter(
        (coupon) => coupon.usage_limit == null || coupon.usage_limit >= min
      );
    }

    if (maxUsageLimit) {
      const max = Number(maxUsageLimit);
      filtered = filtered.filter(
        (coupon) => coupon.usage_limit != null && coupon.usage_limit <= max
      );
    }

    setFilteredCoupons(filtered);
  }, [coupons, searchName, searchCode, typeFilter, statusFilter, minUsageLimit, maxUsageLimit]);

  const clearFilters = () => {
    setSearchName("");
    setSearchCode("");
    setTypeFilter("all");
    setStatusFilter("all");
    setMinUsageLimit("");
    setMaxUsageLimit("");
  };

  const viewDetails = (coupon: any) => {
    window.location.hash = `coupon-management/${coupon._id}`;
  };

  const navigateToEdit = (coupon: any) => {
    window.location.hash = `coupon-management/${coupon._id}/edit`;
  };

  const handleToggleStatus = async (coupon: any) => {
    try {
      const response = await couponsApi.toggleStatus(coupon._id);
      toast[response.success ? "success" : "error"](
        response.message || (coupon.disabled ? "Coupon enabled" : "Coupon disabled")
      );
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to update coupon status"));
    }
  };

  const valueDisplay = (coupon: any) =>
    coupon.isFreeGrant
      ? `${coupon.licenses ?? "—"} lic · ${coupon.dataLimitGB ?? "—"} GB · ${coupon.durationDays ?? "—"}d`
      : coupon.discountType === "$"
        ? `$${coupon.discountAmount ?? 0}`
        : `${coupon.discountPercent ?? 0}%`;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TabIconBadge icon={Tag} accent="coupons" />
          <div>
            <h2 className="text-lg font-semibold">Organizations Coupon</h2>
            <p className="text-sm text-muted-foreground">
              Discount coupons and free-grant licenses available on the platform — {coupons.length} total.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className={TAB_ACCENTS.coupons.button}>
                <Plus className="h-4 w-4 mr-2" />
                Add Coupon
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => (window.location.hash = "coupon-management/new/discount")}
              >
                <Tag className="h-4 w-4 mr-2" />
                Discount Coupon
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => (window.location.hash = "coupon-management/new/free-grant")}
              >
                <Gift className="h-4 w-4 mr-2" />
                Free Grant
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {error && <ErrorBanner message={error} onRetry={() => load()} />}

      <p className="text-sm text-muted-foreground">
        Showing {filteredCoupons.length} of {coupons.length} coupons
      </p>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Coupon List</CardTitle>
              <CardDescription>Filter and search through all coupons.</CardDescription>
            </div>
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div>
              <label className="text-sm mb-2 block">Coupon Name</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search name..."
                  value={searchName}
                  onChange={(event) => setSearchName(event.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="text-sm mb-2 block">Coupon Code</label>
              <Input
                placeholder="Search code..."
                value={searchCode}
                onChange={(event) => setSearchCode(event.target.value)}
              />
            </div>

            <div>
              <label className="text-sm mb-2 block">Type</label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="discount">Discount Coupons</SelectItem>
                  <SelectItem value="free-grant">Free Grants</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm mb-2 block">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm mb-2 block">Min Usage Limit</label>
              <Input
                type="number"
                placeholder="Min limit"
                value={minUsageLimit}
                onChange={(event) => setMinUsageLimit(event.target.value)}
              />
            </div>

            <div>
              <label className="text-sm mb-2 block">Max Usage Limit</label>
              <Input
                type="number"
                placeholder="Max limit"
                value={maxUsageLimit}
                onChange={(event) => setMaxUsageLimit(event.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <TableSkeleton cols={9} />
      ) : (
        <Card className={TAB_ACCENTS.coupons.border}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Coupon</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Discount / Grant</TableHead>
                <TableHead>Window</TableHead>
                <TableHead>Usage</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {filteredCoupons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-10 text-muted-foreground">
                    No coupons found matching your filters.
                  </TableCell>
                </TableRow>
              ) : (
                filteredCoupons.map((coupon: any) => (
                  <TableRow key={coupon._id}>
                    <TableCell className="font-medium max-w-[180px] truncate">
                      {coupon.orgcoupon_name}
                    </TableCell>

                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        {coupon.coupon_code}
                      </Badge>
                    </TableCell>

                    <TableCell>
                      {coupon.isFreeGrant ? (
                        <Badge variant="secondary" className="gap-1">
                          <Gift className="h-3 w-3" />
                          Free Grant
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1">
                          <Tag className="h-3 w-3" />
                          Discount
                        </Badge>
                      )}
                    </TableCell>

                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {valueDisplay(coupon)}
                    </TableCell>

                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {coupon.starts_at ? formatDate(coupon.starts_at) : "?"}
                      {" → "}
                      {coupon.expires_at ? formatDate(coupon.expires_at) : "?"}
                    </TableCell>

                    <TableCell className="text-sm text-muted-foreground">
                      {coupon.limit_used ?? 0}
                      {coupon.usage_limit != null ? ` / ${coupon.usage_limit}` : " / ∞"}
                    </TableCell>

                    <TableCell>
                      {!coupon.organizationIds || coupon.organizationIds.length === 0 ? (
                        <Badge variant="secondary">Global</Badge>
                      ) : (
                        <Badge variant="outline">{coupon.organizationIds.length} orgs</Badge>
                      )}
                    </TableCell>

                    <TableCell>
                      <div className="flex flex-col gap-1 items-start">
                        <StatusBadge disabled={!!coupon.disabled} />
                        {!coupon.isFreeGrant && coupon.stripePromotionCodeId && (
                          <Badge
                            variant="outline"
                            className="text-xs gap-1 text-muted-foreground"
                            title="Redeemable as a Stripe Promotion Code"
                          >
                            <CreditCard className="h-3 w-3" />
                            Stripe
                          </Badge>
                        )}
                        {getCouponStatusKey(coupon) === "expired" && (
                          <Badge variant="outline" className="text-xs gap-1 text-amber-600 border-amber-300">
                            Expired
                          </Badge>
                        )}
                      </div>
                    </TableCell>

                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />

                          <DropdownMenuItem onClick={() => viewDetails(coupon)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>

                          <DropdownMenuItem onClick={() => navigateToEdit(coupon)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>

                          {!coupon.disabled && (
                            <DropdownMenuItem onClick={() => setShareTarget(coupon)}>
                              <Share2 className="h-4 w-4 mr-2" />
                              Share Link
                            </DropdownMenuItem>
                          )}

                          <DropdownMenuItem onClick={() => handleToggleStatus(coupon)}>
                            <Ban className="h-4 w-4 mr-2" />
                            {coupon.disabled ? "Enable" : "Disable"}
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => setDeleteTarget(coupon)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      <DeleteCouponDialog
        coupon={deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onDeleted={() => {
          setDeleteTarget(null);
          load();
        }}
        onDisableInstead={handleToggleStatus}
      />

      <ShareCouponDialog
        coupon={shareTarget}
        open={!!shareTarget}
        onOpenChange={(open) => !open && setShareTarget(null)}
        onSaved={() => load()}
      />
    </div>
  );
}
